import React, { useMemo } from 'react';
import { AutoResizer } from 'react-base-table';
import { isEmpty } from 'lodash';
import { Retry } from '@components/common';
import { TableWrapper, StyledBaseTable } from './styled';
import useFetchData from './useFetchData';
import { buildColumns } from './utils';
import SortIndicator from './SortIndicator';
import TableSkeleton from '@src/components/common/Skeleton/TableSkeleton';
import useSetExport from '@src/components/export/useSetExport';

function Table() {
  const columns: any = buildColumns();

  const { isIdle, status, rows, refetch, sortBy, onColumnSort } = useFetchData();

  // Export data - only when data is loaded and available
  const exportHeaders = useMemo(() => {
    return columns.map((col: any, index: number) => ({
      // First column key is VIEWER_ID, rename it to "name"
      key: index === 0 ? 'name' : col.key,
      // First column is VIEWER_ID, rename title to "Name"
      title: index === 0 ? 'Name' : col.title,
    }));
  }, [columns]);

  const exportData = useMemo(() => {
    if (!rows || isEmpty(rows) || status === 'loading' || isIdle) return [];
    return rows.map((row: any) => {
      const exportRow: any = {};
      columns.forEach((col: any, index: number) => {
        // First column should use 'name' as key instead of VIEWER_ID
        const exportKey = index === 0 ? 'name' : col.key;
        exportRow[exportKey] = row[col.key];
      });
      return exportRow;
    });
  }, [rows, columns, status, isIdle]);

  useSetExport({
    pageKey: 'overview',
    sectionKey: 'overview-dimension-tables',
    widgetKey: 'dimension-viewers-table',
    widgetInfo: { title: 'Top Impacted Viewers' },
    headers: exportHeaders,
    data: exportData,
  });

  return (
    <TableWrapper>
      <Retry
        isLoading={status === 'loading' || isIdle}
        showError={status === 'error'}
        isEmpty={isEmpty(rows)}
        mountChildAlways={false}
        fetchData={refetch}
        LoadingLayer={TableSkeleton}
      >
        <AutoResizer>
          {({ width, height }) => (
            <StyledBaseTable
              data={rows}
              columns={columns}
              headerHeight={50}
              rowHeight={45}
              fixed={false}
              width={width}
              height={height}
              rowKey="VIEWER_ID"
              sortBy={sortBy}
              onColumnSort={onColumnSort}
              components={{
                SortIndicator,
              }}
            />
          )}
        </AutoResizer>
      </Retry>
    </TableWrapper>
  );
}

export default Table;
